import React from 'react';
import { View, Text } from 'react-native';
import {
  LogIn,
  MessageSquare,
  Upload,
  Star,
  CheckCircle2,
  Eye,
  Bug,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import type { UserActivityEvent } from '@/lib/admin/types';

const EVENT_CONFIG: Record<string, { icon: typeof LogIn; color: string }> = {
  login: { icon: LogIn, color: '#007AFF' },
  feedback: { icon: Bug, color: '#FF9500' },
  message: { icon: MessageSquare, color: '#00B8B8' },
  upload: { icon: Upload, color: '#34C759' },
  score_update: { icon: Star, color: '#FF9500' },
  onboarding: { icon: CheckCircle2, color: '#34C759' },
  data_room_view: { icon: Eye, color: '#007AFF' },
};

function formatTime(dateString: string): string {
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface UserActivityTimelineProps {
  events: UserActivityEvent[];
}

export function UserActivityTimeline({ events }: UserActivityTimelineProps) {
  const colors = useThemeColors();

  if (events.length === 0) {
    return (
      <View className="py-6 items-center">
        <Text style={{ color: colors.text.tertiary }} className="text-sm">
          No recent activity
        </Text>
      </View>
    );
  }

  return (
    <View className="gap-0">
      {events.map((event, i) => {
        const config = EVENT_CONFIG[event.event_type] ?? EVENT_CONFIG.login;
        const Icon = config.icon;
        const isLast = i === events.length - 1;

        return (
          <View key={event.id} className="flex-row">
            {/* Timeline line + dot */}
            <View className="items-center mr-3" style={{ width: 28 }}>
              <View
                className="w-7 h-7 rounded-full items-center justify-center z-10"
                style={{ backgroundColor: `${config.color}15` }}
              >
                <Icon size={13} color={config.color} />
              </View>
              {!isLast && (
                <View
                  className="flex-1"
                  style={{
                    width: 1.5,
                    backgroundColor: colors.border.light,
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                />
              )}
            </View>

            {/* Content */}
            <View className="flex-1 pb-4">
              <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                {event.description}
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                {formatTime(event.timestamp)}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}
